import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Paragraph, QuillText, Tooltip, tokens } from '@treams/treams-ui';

const Bullet = styled(Flex)<{
	filled?: boolean;
	party: graphql.EvaluationSubmissionParty;
	enabled: boolean;
}>`
	width: 32px;
	height: 32px;
	border-radius: 16px;
	border: 1px solid #dae0e8;
	overflow: hidden;

	${(props) => (props.enabled ? `cursor: pointer` : `opacity: 0.25`)};

	${(props) =>
		props.filled &&
		`border: none; background: ${
			props.party === graphql.EvaluationSubmissionParty.Employee
				? props.theme.colors.employee
				: props.theme.colors.manager
		}`};
`;

const Dot = styled.div<{ filled: boolean }>`
	position: absolute;
	left: 12px;
	top: 12px;
	width: 8px;
	height: 8px;
	background: ${(props) => (props.filled ? 'rgba(0, 0, 0, 0.1)' : '#dae0e8')};
	border-radius: 8px;
`;

const Label = styled.div`
	color: ${tokens.text.secondary};
	text-overflow: ellipsis;
	overflow: hidden;
`;

const TooltipTitle = styled.div`
	border-top: 1px solid ${tokens.border.subtle};
	margin-top: ${tokens.gap.element}px;
	padding-top: ${tokens.padding.compact}px;
	text-align: center;
`;

function CompetencyInputLevel(props: {
	index: number;
	name: string;
	level: graphql.CompetencyLevelOption;
	party: graphql.EvaluationSubmissionParty;
	suffix?: string;
	withoutDescription?: boolean;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const [, { value }, { setValue }] = useField(props.name + (props.suffix ?? '.competency'));

	return (
		<Box
			mx={1}
			style={{ position: 'relative' }}
			onClick={() => props.level.enabled && setValue(props.index)}
		>
			<Tooltip
				enabled={props.level.enabled}
				content={
					props.withoutDescription ? (
						<Paragraph>{localize(props.level.level.title)?.text}</Paragraph>
					) : (
						<>
							<QuillText
								value={localize(props.level.criteria ?? [])?.text}
								placeholder={t('evaluation.submission.competency.levels.noDescription')}
							/>
							<TooltipTitle>{localize(props.level.level.title)?.text}</TooltipTitle>
						</>
					)
				}
			>
				<Bullet filled={value === props.index} party={props.party} enabled={props.level.enabled}>
					<Dot filled={value === props.index} />
				</Bullet>
			</Tooltip>
		</Box>
	);
}

export function CompetencyLevelsInput(props: {
	name: string;
	withoutDescription?: boolean;
	levels: Array<graphql.CompetencyLevelOption>;
	party: graphql.EvaluationSubmissionParty;
	suffix?: string;
}) {
	const { localize } = useLocalize();

	const { levels } = props;
	// Use enabled levels for the left and right legend text.
	const enabledLevels = levels.filter((level) => !!level.enabled);

	const bullets = levels
		.map<[graphql.CompetencyLevelOption, number]>((level, i) => [level, i])
		.filter(([level]) => level.enabled)
		.map(([level, i]) => (
			<CompetencyInputLevel
				key={i}
				index={i}
				level={level}
				name={props.name}
				suffix={props.suffix}
				party={props.party}
				withoutDescription={props.withoutDescription}
			/>
		));

	return (
		<Flex alignItems="center" justifyContent="space-between">
			{enabledLevels.length > 0 && (
				<Box mr={2}>
					<Label>{localize(enabledLevels[0].level.title).text}</Label>
				</Box>
			)}
			{bullets}
			{enabledLevels.length > 0 && (
				<Box ml={2}>
					<Label>{localize(enabledLevels[enabledLevels.length - 1].level.title).text}</Label>
				</Box>
			)}
		</Flex>
	);
}

export default function CompetencyLevels(props: {
	name: string;
	initialValue?: number;
	competency: graphql.EvaluationCompetencyItem;
	party: graphql.EvaluationSubmissionParty;
}) {
	return (
		<CompetencyLevelsInput
			name={props.name}
			suffix=""
			levels={props.competency.competency.levels}
			party={props.party}
		/>
	);
}
